import { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Container, VStack, useToast } from '@chakra-ui/react';

import { apiGetPublicDeferredDocuments, apiPublicUploadDocument } from 'api';
import PolicyPilotLogo from 'assets/img/others/policy_pilot.png';

import {
  Header,
  Footer,
  SuccessState,
  DocumentTable,
  LoadingState,
  ErrorState,
} from './components';
import type { DeferredDocument } from 'types/documents';

const PublicUploads = () => {
  const { uuid } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { error, data, isLoading } = useQuery({
    queryKey: ['public-deferred-documents', uuid],
    queryFn: () => apiGetPublicDeferredDocuments(uuid!),
  }) as {
    data: {
      applicant: { name: string; policy_no_assigned: string };
      deferred_documents: DeferredDocument[];
    };
    isLoading: boolean;
    error: any;
  };

  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const hasDeferredDocs =
    data && data.deferred_documents && data.deferred_documents.length > 0;

  const pendingDocs =
    data?.deferred_documents?.filter(
      (doc: DeferredDocument) => doc.status === 'pending',
    ) || [];

  const handleUploadClick = (docKey: string) => {
    const fileInput = fileInputRefs.current[docKey];
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
    doc: DeferredDocument,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const docKey = `${doc.validation_key}-${doc.item_key}`;
    setUploadingDoc(docKey);

    try {
      await apiPublicUploadDocument(
        uuid!,
        doc.validation_key,
        doc.item_key,
        file,
        doc.type,
      );

      toast({
        title: 'File uploaded successfully',
        description: `"${doc.text}" has been uploaded`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Refetch the deferred documents list to update the UI
      queryClient.invalidateQueries({
        queryKey: ['public-deferred-documents', uuid],
      });

      // Reset file input
      event.target.value = '';
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: 'Failed to upload the document. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setUploadingDoc(null);
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    const errorMessage =
      error.error ||
      'An error occurred. Please contact your broker for assistance.';
    const isExpired = error?.response?.status === 410;

    return (
      <ErrorState
        title={isExpired ? 'Link Expired' : 'Error'}
        message={errorMessage}
      />
    );
  }

  return (
    <Container maxW="container.xl" py={10}>
      <VStack spacing={6} align="stretch">
        {/* Header with branding and client info */}
        <Header
          organizationLogo={PolicyPilotLogo}
          organizationName="PolicyPilot"
          clientName={data?.applicant?.name || 'Unknown'}
          policyNumber={data?.applicant?.policy_no_assigned || 'Unknown'}
        />

        {/* Success State or Document List */}
        {!hasDeferredDocs ? (
          <SuccessState
            title="All Documents Received!"
            description="All required documents have been received. Thank you for your submission!"
          />
        ) : pendingDocs.length === 0 ? (
          <SuccessState
            title="All Pending Documents Uploaded!"
            description="All pending documents have been successfully uploaded. Your broker will review them shortly."
          />
        ) : (
          <DocumentTable
            documents={data.deferred_documents}
            uploadingDoc={uploadingDoc}
            fileInputRefs={fileInputRefs}
            onUploadClick={handleUploadClick}
            onFileSelect={handleFileSelect}
          />
        )}

        {/* Footer with branding */}
        <Footer
          organizationLogo={PolicyPilotLogo}
          organizationName="PolicyPilot"
        />
      </VStack>
    </Container>
  );
};

export default PublicUploads;
